import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { OdService } from "@/services/Od/OdService";
import { ThrowError } from "@/api/callApi";
import moment from "moment";
import {
  ArrowLeft,
  Calendar,
  FileText,
  Info,
  User,
} from "lucide-react";
import { showToastNotification, formatStatus, formatDuration } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import type { OutdoorDuty } from "@/interface/od.interface";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import FullPageLoader from "@/components/common/FullPageLoader";


const OdDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { siteId } = useCurrentUser();
  const [od, setOd] = useState<OutdoorDuty | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // const [isFatchLoading, setIsFatchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isTeamOd = location.pathname.includes("team-od-detail");

  // For admin actions
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // const isAdmin = Number(user?.type) === 1;
  // const hasOdPermission =
  //   user?.designation?.menuBar?.keyNames?.includes(
  //     permissionKeys.od, // "od"
  //   );
  // const canApproveReject = isAdmin || hasOdPermission;

  const fetchOd = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await OdService.getById(id, siteId);
      if (response?.status && response.data) {
        setOd(response.data);
        if (response.data.remarks) {
          setRemarks(response.data.remarks);
        }
      }
    } catch (error) {
      ThrowError(error);
      setError("Failed to load OD details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOd();
  }, [id]);

  const handleUpdateStatus = async (newStatus: "APPROVED" | "REJECTED") => {
    if (!id) return;
    try {
      setIsSubmitting(true);
      const payload = {
        status: newStatus,
        remarks: remarks,
        siteId: siteId,
      };
      const response = await OdService.updateStatus(id, payload);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || `OD ${newStatus.toLowerCase()} successfully`
        );
        navigate(RoutesLink.teamOd)
        fetchOd(); // refresh details
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to update OD status"
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    );
  }

  if (error || !od) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-700">
            {error || "OD details not found"}
          </h3>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </Layout>
    );
  }

  const status = od.status?.toLowerCase() || "pending";
  let statusClasses = "text-amber-700 bg-amber-50 border-amber-200";
  if (status === "approved") {
    statusClasses = "text-green-700 bg-green-50 border-green-200";
  } else if (status === "rejected" || status === "cancelled") {
    statusClasses = "text-destructive bg-destructive/10 border-destructive/20";
  }

  const isPending = od.status === "PENDING";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <MainHeading
            title="OD Detail"
            isBackButtonVisible
            onClick={() => navigate(-1)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Info className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  OD Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <InfoItem
                  label="Start Date"
                  value={od.startDate ? moment(od.startDate).format("DD-MM-YYYY") : "—"}
                />
                <InfoItem
                  label="End Date"
                  value={od.endDate ? moment(od.endDate).format("DD-MM-YYYY") : "—"}
                />
                <InfoItem label="Location" value={od.location} />
                <InfoItem label="Purpose / Reason" value={od.reason} />
                <InfoItem
                  label="Expected Return Time"
                  value={od.expectedReturnTime}
                />
                <InfoItem
                  label="Duration"
                  value={formatDuration(od.duration)}
                />
                <InfoItem
                  label="Number of Days"
                  value={od.numberOfDays}
                />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${statusClasses}`}
                  >
                    {formatStatus(od.status || "PENDING")}
                  </span>
                </div>
              </div>
            </Card>

            {od.remarks && status !== "pending" && (
              <Card>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Admin Remarks
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {od.remarks}
                </p>
              </Card>
            )}

            {/* Action Card – shown only if user can approve/reject and status is PENDING */}
            {isTeamOd && isPending && (
              <Card className="border border-slate-100 bg-linear-to-b from-white to-slate-50/20">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <Info className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Review Actions
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
                      Admin Remarks
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter remarks for approval or rejection..."
                      className="w-full min-h-[90px] text-sm p-3 bg-white border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-3 font-semibold">
                    <Button
                      variant="destructive"
                      className="px-5 py-2 h-10 shadow-sm"
                      disabled={isSubmitting}
                      onClick={() => handleUpdateStatus("REJECTED")}
                    >
                      Reject
                    </Button>
                    <Button
                      className="px-5 py-2 h-10 shadow-sm"
                      disabled={isSubmitting}
                      onClick={() => handleUpdateStatus("APPROVED")}
                    >
                      Approve
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <User className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Applicant Details
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem label="Employee Name" value={od.user?.name || "—"} />
                <InfoItem label="Email Address" value={od.user?.email || "—"} />
                <InfoItem label="Phone" value={od.user?.phone || "—"} />
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <User className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Action Taken By
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem label="Employee Name" value={od.actionUser?.name || "—"} />
                <InfoItem label="Email Address" value={od.actionUser?.email || "—"} />
                <InfoItem label="Phone" value={od.actionUser?.phone || "—"} />
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Timeline
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Applied On"
                  value={
                    od.createdAt
                      ? moment(od.createdAt).format("DD-MM-YYYY HH:mm")
                      : "—"
                  }
                />
                {od.updatedAt && od.updatedAt !== od.createdAt && (
                  <InfoItem
                    label="Last Updated"
                    value={moment(od.updatedAt).format("DD-MM-YYYY HH:mm")}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-sm font-semibold text-foreground">
      {value || "—"}
    </p>
  </div>
);

export default OdDetail;